import * as XLSX from 'xlsx';
import JSZip from 'jszip';

// Columns required to create a master product (matches add-item form + item/add).
// productitem_id is generated by the API (PI-######). expire/lot are deferred (D3 master UI hidden).
export const PRODUCT_IMPORT_COLUMNS = [
  {
    header: 'รหัสบาร์โค้ด',
    description: 'รหัสบาร์โค้ดสินค้า อย่างน้อย 5 ตัวอักษร',
    sample: '8850000000001',
  },
  {
    header: 'หมวดหมู่สินค้า',
    description: 'เลือกจาก dropdown ในไฟล์ตัวอย่าง — ใช้ชื่อหมวดหมู่ที่มีในระบบ',
    sample: 'ยาสามัญประจำบ้าน',
  },
  {
    header: 'ชื่อสินค้าภาษาไทย',
    description: 'อย่างน้อย 5 ตัวอักษร เช่น ไพลว่าน่าครีม Plai Oil 14% 15g',
    sample: 'ไพลว่าน่าครีม Plai Oil 14% 15g',
  },
  {
    header: 'ชื่อสินค้าภาษาอังกฤษ',
    description: 'อย่างน้อย 5 ตัวอักษร เช่น Plaivana Cream Plai Oil 14% 15g',
    sample: 'Plaivana Cream Plai Oil 14% 15g',
  },
  {
    header: 'รายละเอียดสินค้า',
    description:
      'อย่างน้อย 5 ตัวอักษร เช่น ไทลินอล ชนิดเม็ด มีตัวยาสำคัญ Paracetamol 500 mg ใช้สำหรับบรรเทาอาการปวดเล็กน้อยถึงปานกลาง',
    sample:
      'ไทลินอล ชนิดเม็ด มีตัวยาสำคัญ Paracetamol 500 mg ใช้สำหรับบรรเทาอาการปวดเล็กน้อยถึงปานกลาง',
  },
  {
    header: 'ความกว้าง',
    description: 'หน่วย เซนติเมตร (cm)',
    sample: '5',
  },
  {
    header: 'ความยาว',
    description: 'หน่วย เซนติเมตร (cm)',
    sample: '10',
  },
  {
    header: 'ความสูง',
    description: 'หน่วย เซนติเมตร (cm)',
    sample: '8',
  },
  {
    header: 'น้ำหนัก',
    description: 'หน่วย กรัม (g)',
    sample: '15',
  },
];

export const PRODUCT_IMPORT_TEMPLATE_FILENAME = 'meya-product-import-template.xlsx';
export const PRODUCT_IMPORT_MAX_ROWS = 5000;
export const PRODUCT_IMPORT_SHEET_NAME = 'สินค้า';
export const PRODUCT_IMPORT_CATEGORY_SHEET_NAME = '_categories';
export const PRODUCT_IMPORT_CATEGORY_HEADER = 'หมวดหมู่สินค้า';

function escapeCsvCell(cell) {
  return `"${String(cell).replace(/"/g, '""')}"`;
}

function columnLetter(index) {
  let letter = '';
  let n = index;
  while (n >= 0) {
    letter = String.fromCharCode((n % 26) + 65) + letter;
    n = Math.floor(n / 26) - 1;
  }
  return letter;
}

export function getProductImportCategoryNames(categories = []) {
  return [...categories]
    .filter((category) => (category?.productcategory_name || '').trim())
    .sort((a, b) => (a.productcategory_sortno || 0) - (b.productcategory_sortno || 0))
    .map((category) => category.productcategory_name.trim())
    .filter((name, index, names) => names.indexOf(name) === index);
}

export function buildProductImportTemplateCsv(categories = []) {
  const categoryNames = getProductImportCategoryNames(categories);
  const headers = PRODUCT_IMPORT_COLUMNS.map((column) => column.header);
  const sampleRow = PRODUCT_IMPORT_COLUMNS.map((column) => {
    if (column.header === PRODUCT_IMPORT_CATEGORY_HEADER) {
      return categoryNames[0] || column.sample;
    }
    return column.sample;
  });
  return [headers, sampleRow].map((row) => row.map(escapeCsvCell).join(',')).join('\n');
}

function buildTemplateSheetData(categoryNames) {
  const headers = PRODUCT_IMPORT_COLUMNS.map((column) => column.header);
  const sampleRow = PRODUCT_IMPORT_COLUMNS.map((column) => {
    if (column.header === PRODUCT_IMPORT_CATEGORY_HEADER) {
      return categoryNames[0] || '';
    }
    return column.sample;
  });
  return [headers, sampleRow];
}

function buildCategoryDropdownXml(categoryCount) {
  const categoryColumnIndex = PRODUCT_IMPORT_COLUMNS.findIndex(
    (column) => column.header === PRODUCT_IMPORT_CATEGORY_HEADER,
  );
  const column = columnLetter(categoryColumnIndex);
  const lastRow = PRODUCT_IMPORT_MAX_ROWS + 1;
  const formula = `'${PRODUCT_IMPORT_CATEGORY_SHEET_NAME}'!$A$1:$A$${categoryCount}`;
  return (
    `<dataValidations count="1">` +
    `<dataValidation type="list" allowBlank="1" showInputMessage="1" showErrorMessage="1" ` +
    `sqref="${column}2:${column}${lastRow}" errorStyle="stop" errorTitle="หมวดหมู่สินค้า" ` +
    `error="กรุณาเลือกหมวดหมู่จากรายการในระบบ">` +
    `<formula1>${formula}</formula1>` +
    `</dataValidation>` +
    `</dataValidations>`
  );
}

async function injectCategoryDropdown(xlsxBuffer, categoryCount) {
  const zip = await JSZip.loadAsync(xlsxBuffer);
  const sheetPath = 'xl/worksheets/sheet1.xml';
  const sheetFile = zip.file(sheetPath);
  if (!sheetFile) {
    return toUint8Array(xlsxBuffer);
  }

  let xml = await sheetFile.async('string');
  const dropdownXml = buildCategoryDropdownXml(categoryCount);
  if (xml.includes('<dataValidations')) {
    xml = xml.replace(/<dataValidations[\s\S]*?<\/dataValidations>/, dropdownXml);
  } else if (xml.includes('</sheetData>')) {
    xml = xml.replace('</sheetData>', `</sheetData>${dropdownXml}`);
  } else {
    xml = xml.replace('</worksheet>', `${dropdownXml}</worksheet>`);
  }
  zip.file(sheetPath, xml);
  const output = await zip.generateAsync({
    type: 'uint8array',
    compression: 'DEFLATE',
  });
  return output;
}

function toUint8Array(data) {
  if (data instanceof Uint8Array) {
    return data;
  }
  return new Uint8Array(data);
}

export async function buildProductImportTemplateXlsx(categories = []) {
  const categoryNames = getProductImportCategoryNames(categories);
  const workbook = XLSX.utils.book_new();
  const importSheet = XLSX.utils.aoa_to_sheet(buildTemplateSheetData(categoryNames));
  importSheet['!cols'] = PRODUCT_IMPORT_COLUMNS.map((column) => ({
    wch: Math.max(16, column.header.length + 6),
  }));
  XLSX.utils.book_append_sheet(workbook, importSheet, PRODUCT_IMPORT_SHEET_NAME);

  if (categoryNames.length > 0) {
    const categorySheet = XLSX.utils.aoa_to_sheet(categoryNames.map((name) => [name]));
    XLSX.utils.book_append_sheet(workbook, categorySheet, PRODUCT_IMPORT_CATEGORY_SHEET_NAME);
    workbook.Workbook = {
      Sheets: [{ Hidden: 0 }, { Hidden: 1 }],
    };
  }

  const xlsxBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  if (categoryNames.length === 0) {
    return toUint8Array(xlsxBuffer);
  }
  return injectCategoryDropdown(xlsxBuffer, categoryNames.length);
}
